import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { finalize, forkJoin, map, Observable, of, switchMap, tap, catchError } from 'rxjs';
import { patch } from '@ngxs/store/operators';
import { EscalationService } from '../services/escalation.service';
import { ClearEscalationsCache, LoadEscalations, SnoozeEscalation } from './escalation.actions';
import { Escalation } from '../models/escalations.model';
import { PageMetadata, Paginated } from '../../shared/models/paginated.model';
import { TeamService } from '../../shared/services/team.service';
import { EscalationView } from '../models/escalation-view.model';
import { EscalationParentType } from '@shs-digi-care-monorepo/interfaces';
import { UserService } from '../../auth/services/user.service';
import { NamRequestService } from '../../../nam/services/request.service';

export interface EscalationsStateModel {
    escalations: {
        dataByPage: { [page: number]: EscalationView[] };
        metadata: PageMetadata;
    };
    areEscalationsLoading: boolean;
}

@State<EscalationsStateModel>({
    name: 'escalationsState',
    defaults: {
        escalations: {
            dataByPage: {},
            metadata: {
                hasNextPage: false,
                itemCount: -1,
                take: 20,
                currentPage: -1
            }
        },
        areEscalationsLoading: false
    }
})
@Injectable()
export class EscalationState {
    private escalationService = inject(EscalationService);
    private teamService = inject(TeamService);
    private userService = inject(UserService);
    private requestService = inject(NamRequestService);

    @Selector()
    public static totalEscalations(state: EscalationsStateModel): number {
        return state.escalations.metadata.itemCount;
    }

    @Selector()
    public static escalations(state: EscalationsStateModel): EscalationView[] {
        const currentPage = state.escalations.metadata.currentPage || 0;
        return state.escalations.dataByPage[currentPage] ? state.escalations.dataByPage[currentPage] : [];
    }

    @Selector()
    public static areEscalationsLoading(state: EscalationsStateModel): boolean {
        return state.areEscalationsLoading;
    }

    @Action(LoadEscalations)
    public loadEscalations(
        { getState, setState, patchState }: StateContext<EscalationsStateModel>,
        { payload }: LoadEscalations
    ): Observable<Paginated<EscalationView>> {
        const state = getState();
        const requestedPage = payload?.page ?? state.escalations.metadata.currentPage;

        patchState({
            areEscalationsLoading: true,
            escalations: {
                ...state.escalations,
                metadata: {
                    ...state.escalations.metadata,
                    currentPage: requestedPage
                }
            }
        });
        if (!payload?.reset) {
            if (state.escalations.dataByPage[requestedPage] && state.escalations.dataByPage[requestedPage].length > 0) {
                patchState({ areEscalationsLoading: false });
                return of({
                    items: state.escalations.dataByPage[requestedPage] || [],
                    metadata: {
                        ...state.escalations.metadata,
                        currentPage: requestedPage
                    }
                });
            }
        }

        return this.escalationService.getEscalations(requestedPage).pipe(
            switchMap((paginatedResponse: Paginated<Escalation>) => {
                const escalations = paginatedResponse.items;

                const teamRequests = escalations
                    .filter((escalation) => escalation.parentType === EscalationParentType.TEAM)
                    .map((escalation) =>
                        this.teamService.getTeamById(escalation.parentId).pipe(
                            map((team) => ({
                                ...escalation,
                                escalationSource: team.name
                            }))
                        )
                    );

                const userRequests = escalations
                    .filter((escalation) => escalation.parentType === EscalationParentType.USER)
                    .map((escalation) =>
                        this.userService.getByGID(escalation.parentId).pipe(
                            map((user) => ({
                                ...escalation,
                                escalationSource: user.firstName + ' ' + user.lastName
                            }))
                        )
                    );

                return forkJoin([...teamRequests, ...userRequests]).pipe(
                    switchMap((escalationsList) => {
                        const requestObservables = escalationsList.map((escalation) =>
                            this.requestService.getRequestById(escalation.resourceId).pipe(
                                map((request) => ({
                                    ...escalation,
                                    displayId: request.displayId
                                })),
                                catchError(() =>
                                    of({
                                        ...escalation,
                                        displayId: escalation.resourceId
                                    })
                                )
                            )
                        );

                        return forkJoin(requestObservables);
                    }),
                    map((escalationsList) => ({
                        items: escalationsList,
                        metadata: paginatedResponse.metadata
                    }))
                );
            }),
            tap(({ items, metadata }) => {
                const currentState = getState();
                setState(
                    patch({
                        escalations: {
                            dataByPage: {
                                ...currentState.escalations.dataByPage,
                                [requestedPage]: items
                            },
                            metadata: metadata
                        }
                    })
                );
            }),
            finalize(() => patchState({ areEscalationsLoading: false }))
        );
    }

    @Action(ClearEscalationsCache)
    public clearEscalationsCache({ setState }: StateContext<EscalationsStateModel>): void {
        setState({
            escalations: {
                dataByPage: {},
                metadata: {
                    hasNextPage: false,
                    itemCount: -1,
                    take: 10,
                    currentPage: -1
                }
            },
            areEscalationsLoading: false
        });
    }

    @Action(SnoozeEscalation)
    public snoozeEscalation(ctx: StateContext<EscalationsStateModel>, { payload }: SnoozeEscalation): Observable<void> {
        return this.escalationService.snoozeEscalation(payload.id);
    }
}
